import React, { useState, useEffect } from "react";
import { FormRow, FormRowInner, Label, ReadOnly, CanEdit, Note, CountryWrapper, CountryFlag, CallingCodeText } from "../../../styles/forms/inputs";
import InputError from "../errors/input";
import InputHelp from "./help";
import { IHelp } from "../../../state/context/modals.reducers";
import Input from "react-phone-number-input/input";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import parsePhoneNumberFromString, { CountryCode } from "libphonenumber-js";
import en from "react-phone-number-input/locale/en.json";
import * as flags from "country-flag-icons/react/3x2";
import Select, { components, OptionProps, SingleValueProps } from "react-select";
import theme from "../../../styles/theme";
import type { FlagComponent } from "country-flag-icons/react/3x2";

type Props = {
	label?: string | React.ReactNode;
	light?: boolean;
	help?: IHelp;
	placeholder?: string;
	name: string;
	value: string | null | undefined;
	readonly?: boolean;
	error?: string;
	inline?: boolean;
	onChange: (e: { target: { name: string; value: string } }) => void;
	required?: boolean;
	canEditReadOnly?: boolean;
	note?: string;
	flush?: boolean;
};

type CountrySelectProps = {
	value: CountryCode;
	onChange: (value: CountryCode) => void;
};

type Option = {
	value: CountryCode;
	label: string;
	callingCode: string;
};

const countryOptions: Option[] = getCountries().map((code) => ({
	value: code,
	label: en[code],
	callingCode: `+${getCountryCallingCode(code)}`,
}));

const CountryOption = (props: OptionProps<Option, false>) => {
	const { value, label, callingCode } = props.data;
	const Flag = flags[value as keyof typeof flags] as FlagComponent;

	return (
		<components.Option {...props}>
			<CountryWrapper isCenter={true}>
				{Flag && (
					<CountryFlag>
						<Flag />
					</CountryFlag>
				)}
				<span>{label}</span>
				<CallingCodeText>{callingCode}</CallingCodeText>
			</CountryWrapper>
		</components.Option>
	);
};

const CountryValue = (props: SingleValueProps<Option, false>) => {
	const { value, callingCode } = props.data;
	const Flag = flags[value as keyof typeof flags] as FlagComponent;

	return (
		<components.SingleValue {...props}>
			<CountryWrapper isCenter={true}>
				{Flag && (
					<CountryFlag>
						<Flag />
					</CountryFlag>
				)}
				<span>{callingCode}</span>
			</CountryWrapper>
		</components.SingleValue>
	);
};

const customStyles = {
	control: (base) => ({
		...base,
		width: 80,
		height: 53,
		cursor: "pointer",
	}),
	valueContainer: (base) => ({
		...base,
		padding: 0,
		justifyContent: "center",
	}),
	indicatorsContainer: () => ({
		display: "none",
	}),
	menu: (base) => ({
		...base,
		width: 240,
		minWidth: 240,
	}),
	menuList: (base) => ({
		...base,
		maxHeight: 240,
		overflowY: "auto",
		//hide scroll
		scrollbarWidth: "none",
		msOverflowStyle: "none",
		"::-webkit-scrollbar": {
			display: "none",
		},
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 9999,
	}),
	option: (styles, { isFocused, isSelected }) => ({
		...styles,
		backgroundColor: isFocused || isSelected ? theme.quarterinaryGreen : theme.white,
		":hover": {
			backgroundColor: theme.quarterinaryGreen,
		},
		cursor: "pointer",
	}),
};

function CountrySelect({ value, onChange }: CountrySelectProps) {
	const selected = countryOptions.find((c) => c.value === value) || countryOptions.find((c) => c.value === "GB");

	return (
		<Select
			options={countryOptions}
			value={selected}
			onChange={(option) => onChange(option!.value)}
			components={{
				Option: CountryOption,
				SingleValue: CountryValue,
			}}
			isSearchable
			styles={customStyles}
		/>
	);
}

const PhoneTextInput: React.FC<Props> = ({ label, light, help, placeholder, name, value, readonly, error, inline, onChange, required, canEditReadOnly = null, note, flush }) => {
	const parsedInitial = parsePhoneNumberFromString(value ?? "");
	const initialCountry = parsedInitial?.country ?? "GB";

	const [inputValue, setInputValue] = useState(value ?? "");
	const [inputError, setInputError] = useState(error ?? null);
	const [isEditing, setIsEditing] = useState(!readonly && (canEditReadOnly ?? true));
	const [country, setCountry] = useState(initialCountry);

	const displayReadOnly = canEditReadOnly === false ? true : !isEditing;

	const handleChange = (val: string) => {
		setInputValue(val);
		onChange({ target: { name, value: val } });
	};

	const handleBlur = () => {
		if (!inputValue) {
			setInputError(null);
			return;
		}

		const parsed = parsePhoneNumberFromString(inputValue, country);
		if (!parsed || !parsed.isValid()) {
			setInputError("Please enter a valid phone number.");
			return;
		}

		onChange({ target: { name, value: parsed.number } });
		setInputError(null);
	};

	useEffect(() => {
		if (value) {
			let newValue = value;
			const parsed = parsePhoneNumberFromString(value);

			if (parsed?.country) {
				setCountry(parsed.country);
			} else {
				setCountry("GB");
				if (!value.startsWith("+")) {
					newValue = `+44${value.replace(/^0/, "")}`;
				}
			}

			setInputValue(newValue);
		}
		setInputError(error ?? null);
	}, [error, value]);

	const handleCountryChange = (newCountry: CountryCode) => {
		setCountry(newCountry);
	};

	return (
		<FormRow hasError={!!inputError} flush={flush}>
			<FormRowInner type={null} readonly={displayReadOnly}>
				{label && (
					<Label light={light ?? false} help={help}>
						{label}
						{required ? "*" : null}
						{help ? <InputHelp help={help} /> : null}
					</Label>
				)}
				{note && <Note>{note}</Note>}

				{displayReadOnly ? (
					<ReadOnly canEdit={canEditReadOnly}>
						{value}
						{canEditReadOnly ? <CanEdit onClick={() => setIsEditing(true)} /> : null}
					</ReadOnly>
				) : (
					<CountryWrapper isCenter={false}>
						<CountrySelect value={country} onChange={handleCountryChange} />
						<Input name={name} placeholder={placeholder} value={inputValue} country={country} onChange={handleChange} onBlur={handleBlur} />
					</CountryWrapper>
				)}
			</FormRowInner>

			{inputError && <InputError message={inputError} inline={inline} />}
		</FormRow>
	);
};

export default PhoneTextInput;
